import { Subject } from 'rxjs';
import { CoordinatorService } from './coordinator.service';
import { V, View } from './view';
import { XItem } from './x-item';
import { SubsetConnector } from './calculation-classes';

export type VPAR = {
    id: string;
    title: string;
    xitmIdLs: string[];
    isOn?: boolean;
};

export class View$ {
    private _viewMap = new Map<string, View>();
    private _hitsetMap = new Map<string, Map<string, View>>([
        ['preview', new Map<string, View>()],
        ['main', new Map<string, View>()],
    ]);
    refresh$ = new Subject<string[] | undefined>();

    constructor(public coords: CoordinatorService) {}

    get previewBulkLs() {
        const ls = ['__toMain', '__seen', '__rejected'].map((x) => this.coords.view$.get(x));
        this.sortedViewLs.forEach((x) => ls.push(x));
        return ls;
    }

    checkCollectionItems() {
        this.viewLs.forEach((v) => {
            v.checkCollectionItems();
        });
    }

    markForManifestStore() {
        this._viewMap.forEach((v) => {
            v.markForManifestStore();
        });
    }

    addToMap(v: View) {
        const mp = v.isHitset
            ? this._hitsetMap.get(v.isPreview ? 'preview' : 'main')!
            : this._viewMap;
        const tag = v.id.startsWith('__') ? v.id.split('-')[0] : v.id;
        mp.set(tag, v);
    }

    clear() {
        this._viewMap.clear();
        this._hitsetMap.forEach((m) => m.clear());
    }

    refresh(ls?: string[]) {
        this.refresh$.next(ls);
    }

    load3(ls: any) {
        ls?.forEach((x: any) => {
            try {
                const v = new View(this.coords, x);
            } catch (e) {
                this.coords.utils$.consoleError('VIEW LOAD3 ERROR', e);
            }
        });
    }

    xitemSubsetConnector(xitmId: string) {
        return new SubsetConnector(this.coords, xitmId);
    }

    private _exportParams() {
        const ls: [string, number][] = [];

        this.sortedViewLs.forEach((x) => ls.push([x.title, x.size]));

        return {
            headerLs: ['Subset', 'Count'].map((x) => this.coords.text(x)),
            rowLs: ls,
            filename: `${this.coords.project$.currentTitle}_subsets`,
        };
    }

    html(isPartial = false) {
        this.coords.export$.html(this._exportParams());
    }

    csv(isPartial = false) {
        this.coords.export$.csv(this._exportParams());
    }

    get viewLs() {
        return Array.from(this._viewMap.values())
            .filter((v) => v.isOn)
            .filter((v) => !v.id.startsWith('_'));
    }

    get sortedViewLs() {
        const ls = this.viewLs;
        ls.sort((a, b) => (a?.title?.toLowerCase() < b?.title?.toLowerCase() ? -1 : 1));
        return ls;
    }

    get allViewLs() {
        const ls = Array.from(this._viewMap.values()).filter((v) => !v.id.startsWith('_'));
        ls.sort((a, b) => (a?.title?.toLowerCase() < b?.title?.toLowerCase() ? -1 : 1));
        return ls;
    }

    exportJsonAll() {
        return this.viewLs.map((v) => ({ id: v.id, title: v.title }));
    }

    withIdViewLs(itmId: string) {
        return this.viewLs.filter((v) => v.has(itmId));
    }

    create(params?: V, withRefresh = true) {
        const v = new View(this.coords, params || {});
        withRefresh && this.refresh();
        v.markForManifestStore();
        return v;
    }

    delete(v: View) {
        this._viewMap.delete(v.id);
    }

    get(vid: string) {
        return vid.startsWith('__') ? this.getAlwaysPresentView(vid) : this._viewMap.get(vid);
    }

    getByTitle(title: string) {
        title = this.coords.utils$.normalizeString(title);
        for (const v of this._viewMap.values()) {
            if (v.id.startsWith('_')) continue;
            if (this.coords.utils$.normalizeString(v.title) === title) return v;
        }
        return;
    }

    saveAsScript() {
        const ls: string[] = [];
        const titleTxt = this.sortedViewLs.map((v) => v.title).join(', \n');
        ls.push(`const viewTitles = \`\n${titleTxt}\n\``);
        ls.push("subset(viewTitles.split(','), true)");
        const txt = ls.join('\n');
        this.coords.filesystem$.saveFile2(
            `subsets.mscript`,
            new Blob([txt], { type: 'text/plain' }),
        );
    }

    save(view?: View): VPAR[] {
        if (view) return [view.save()];

        return Array.from(this._viewMap.values()).map((v) => v.save());
    }

    getAlwaysPresentView(tag: string) {
        let v = this._viewMap.get(tag);
        if (!v) {
            v = this.create({ id: tag }, false);
            this._viewMap.set(tag, v);
        }
        return v;
    }

    to(target: string, xitmLs: XItem | XItem[], isOnOnly = false) {
        if (!Array.isArray(xitmLs)) xitmLs = [xitmLs];
        const idLs = xitmLs.map((xitm) => xitm.id);
        // console.log('TO', target, idLs);
        switch (target) {
            case 'main':
                this.coords.dataset$.addXitemLsToMain(xitmLs, this.coords.user$.currentId);
                return;
            default:
                let v: View | undefined;
                if (target.startsWith('__')) v = this.getAlwaysPresentView(target);
                else v = this.get(target);
                if (v) {
                    if (isOnOnly) v.addIds(idLs);
                    else v.switchIds(idLs);
                }
        }

        this.refresh(idLs);
        this.coords.display$.markForCheck();
    }

    in(target: string, memberId: string | string) {
        const v = this._viewMap.get(target);
        if (v && v.has(memberId)) return true;
        return false;
    }

    sortedHitsetLs(tag?: string): View[] {
        if (!tag) tag = this.coords.stage$.isPreview ? 'preview' : 'main';

        const ls = Array.from(this._hitsetMap.get(tag)!.values());
        ls.sort((a, b) => (a.title?.toLowerCase() < b.title?.toLowerCase() ? -1 : 1));
        return ls;
    }

    saveHitset(params: any) {
        params.isHitset = true;
        new View(this.coords, params);
    }

    isValidHitsetTitle(txt: string) {
        return !!txt.trim();
    }

    dialogSearch(isSavedHitset: boolean): void {
        this.coords.display$.dialog$.next({
            action: 'search-view',
            isSavedHitset: isSavedHitset,
        });
    }

    async loadSearchProcedure() {
        const resp = await this.coords.dataset$.current.runSearchProcedure();
        console.log('IDLS', resp);
        this.saveHitset({
            title: resp.title,
            idSet: new Set(resp.resultIdLs),
            isPreview: this.coords.stage$.isPreview,
        });
        this.dialogSearch(true);
    }
}
